import React, { useState } from 'react';
import { Button } from '@/components/ui';
import { TelegramUtils } from '@/utils/telegram';
import { useBlockUser } from '@/hooks/api';
import { api } from '@/api';
import { useAppStore } from '@/store';

interface BlockConfirmModalProps {
  userId: string;
  userName: string;
  matchId?: string;
  onClose: () => void;
  onConfirm: () => void; // Called after successful block
}

const BlockConfirmModal: React.FC<BlockConfirmModalProps> = ({ 
  userId, 
  userName, 
  matchId,
  onClose, 
  onConfirm 
}) => {
  const [alsoUnmatch, setAlsoUnmatch] = useState(!!matchId);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useAppStore();
  
  const blockUser = useBlockUser({
    onSuccess: async () => {
      console.log('✅ Block successful!');
      // If user wants to unmatch and matchId exists, do it
      if (alsoUnmatch && matchId) {
        try {
          console.log('🗑️ Also unmatching:', matchId);
          await (api as any).unmatchUser(Number(matchId));
        } catch (error) {
          console.error('Failed to unmatch:', error);
          // Continue anyway, block was successful
        }
      }
      
      showToast('Пользователь заблокирован', 'info');
      onConfirm();
    },
    onError: (error) => {
      console.error('❌ Block failed:', error);
      setIsProcessing(false);
    }
  });

  const handleBlock = async () => {
    console.log('🚫 Blocking user:', userId);
    setIsProcessing(true);
    blockUser.mutate(userId);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm animate-fade-in safe-area-y">
      <div className="w-full max-w-sm bg-tg-bg border border-white/10 rounded-3xl p-6 animate-scale-in">
        {/* Icon */}
        <div className="mb-6 flex justify-center">
          <div className="w-16 h-16 bg-danger-500/20 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-danger-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
        </div>

        {/* Header */}
        <div className="mb-6 text-center">
          <h2 className="text-2xl font-bold text-white mb-2">
            Заблокировать пользователя?
          </h2>
          <p className="text-neutral-400 text-sm">
            Вы больше не будете видеть профиль <span className="text-white font-medium">{userName}</span> в ленте
          </p>
        </div>

        {/* Checkbox for unmatch */}
        {matchId && (
          <label className="flex items-start gap-3 mb-6 p-3 bg-white/5 border border-white/10 rounded-xl cursor-pointer hover:bg-white/10 transition-colors">
            <input
              type="checkbox"
              checked={alsoUnmatch}
              onChange={(e) => setAlsoUnmatch(e.target.checked)}
              disabled={isProcessing}
              className="mt-0.5 w-5 h-5 rounded border-2 border-neutral-600 bg-transparent checked:bg-primary-500 checked:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:ring-offset-tg-bg transition-all cursor-pointer disabled:opacity-50"
            />
            <div className="flex-1">
              <p className="text-white text-sm font-medium">
                Также удалить из совпадений
              </p>
              <p className="text-neutral-500 text-xs mt-1">
                Ваш чат будет удален
              </p>
            </div>
          </label>
        )}

        {/* Warning */}
        <div className="mb-6 p-3 bg-danger-500/10 border border-danger-500/20 rounded-xl">
          <p className="text-xs text-danger-400">
            ⚠️ Это действие необратимо. Вы сможете разблокировать пользователя только в настройках.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="flex-1 h-12 bg-white/10 border border-white/20 rounded-xl text-white font-medium hover:bg-white/20 transition-all active:scale-95 disabled:opacity-50"
          >
            Отмена
          </button>
          
          <Button
            onClick={handleBlock}
            loading={isProcessing}
            disabled={isProcessing}
            variant="danger"
            className="flex-1 h-12 font-bold rounded-xl"
          >
            Заблокировать
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BlockConfirmModal;

